import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function HalfLifeCalculator() {
  const [order, setOrder] = useState("1");
  const [initialConc, setInitialConc] = useState("");
  const [rateConstant, setRateConstant] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const c0 = parseFloat(initialConc);
    const k = parseFloat(rateConstant);

    if (isNaN(k) || k <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid positive rate constant.",
        variant: "destructive",
      });
      return;
    }

    let halfLife: number;

    if (order === "0") {
      if (isNaN(c0) || c0 <= 0) {
        toast({
          title: "Invalid Input",
          description: "Initial concentration required for zero-order.",
          variant: "destructive",
        });
        return;
      }
      halfLife = c0 / (2 * k);
    } else if (order === "1") {
      halfLife = Math.LN2 / k;
    } else {
      if (isNaN(c0) || c0 <= 0) {
        toast({
          title: "Invalid Input",
          description: "Initial concentration required for second-order.",
          variant: "destructive",
        });
        return;
      }
      halfLife = 1 / (k * c0);
    }

    setResult(halfLife);

    toast({
      title: "Calculation Complete",
      description: `Half-life = ${halfLife.toFixed(4)} s`,
    });
  };

  const reset = () => {
    setOrder("1");
    setInitialConc("");
    setRateConstant("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Half-Life Calculator" 
      calculatorPath="/calculator/half-life"
      category="chemistry"
      subcategory="Kinetics"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Half-Life Calculator</CardTitle>
            <CardDescription>
              Calculate half-life for different reaction orders
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="order">Reaction Order</Label>
                <Select value={order} onValueChange={setOrder}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Zero Order</SelectItem>
                    <SelectItem value="1">First Order</SelectItem>
                    <SelectItem value="2">Second Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {order !== "1" && (
                <div className="space-y-2">
                  <Label htmlFor="c0">Initial Concentration [A]₀ (M)</Label>
                  <Input
                    id="c0"
                    type="number"
                    step="any"
                    value={initialConc}
                    onChange={(e) => setInitialConc(e.target.value)}
                    placeholder="Required for 0th and 2nd order"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="k">Rate Constant (k)</Label>
                <Input
                  id="k"
                  type="number"
                  step="any"
                  value={rateConstant}
                  onChange={(e) => setRateConstant(e.target.value)}
                  placeholder="Enter rate constant"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Half-Life (t₁/₂)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(4)} s</p>
                    <CopyResultButton value={`${result.toFixed(4)} s`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formulas</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p className="font-mono">Zero Order: t₁/₂ = [A]₀/(2k)</p>
                <p className="font-mono">First Order: t₁/₂ = ln(2)/k</p>
                <p className="font-mono">Second Order: t₁/₂ = 1/(k[A]₀)</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
